<template>
  <v-app>
    <v-container
      class="fill-height d-flex flex-column align-stretch pt-2 px-6 pb-6"
      style="background-color: #F8F7F4;"
      :style="navbarWithName
        ? 'margin-left: 145px !important; width: calc(100% - 145px) !important; max-width: calc(100% - 145px) !important;'
        : 'width: 100% !important; max-width: 100% !important;'"
      fluid
    >
      <v-card
        class="brand-wrapper elevation-4"
        :style="{ width: '100%', zoom: uiScale }"
      >
        <div class="brand-header d-flex align-center justify-space-between px-8 py-5">
          <div class="d-flex align-center">
            <v-avatar class="rounded-lg mr-4" color="#2B4A6F" size="56">
              <v-icon color="white" size="32">
                fa-solid fa-chart-simple
              </v-icon>
            </v-avatar>
            <div>
              <div class="text-h5 font-weight-bold color-navy">
                Brand Analysis
              </div>
              <div class="text-body-2 grey--text text--darken-1">
                Comparing Your Brand Against Competitors
              </div>
            </div>
          </div>

          <div class="d-flex align-center" style="gap: 10px;">
            <div class="header-select-wrapper">
              <select
                v-model="selectedGroupAnalysisKey"
                class="header-group-select"
                @change="updateSelectedGroupAnalysis"
              >
                <option value="" disabled hidden>
                  Select Group Analysis
                </option>
                <option
                  v-for="(item, index) in itemGroupAnalysis"
                  :key="index"
                  :value="item.GROUP_ANALYSIS"
                >
                  {{ item.GROUP_ANALYSIS }}
                </option>
              </select>
              <v-icon class="header-chevron-icon" size="20">
                mdi-chevron-down
              </v-icon>
            </div>

            <v-btn
              class="header-apply-btn"
              depressed
              :disabled="isLoadingSlideData || isExporting"
              @click="loadAllSlideData()"
            >
              <v-icon color="white" size="22" left>
                {{ isLoadingSlideData ? 'mdi-loading mdi-spin' : 'mdi-magnify' }}
              </v-icon>
              {{ isLoadingSlideData ? 'Loading...' : 'Apply' }}
            </v-btn>

            <v-btn
              class="header-export-btn"
              depressed
              :disabled="isLoadingSlideData || isExporting"
              @click="exportSlide"
            >
              <v-icon color="black" size="22" left>
                {{ isExporting ? 'mdi-loading mdi-spin' : 'mdi-download' }}
              </v-icon>
              {{ isExporting ? 'Exporting...' : 'Export' }}
            </v-btn>

            <v-btn
              class="header-setting-btn"
              depressed
              @click="settingDialog = true"
            >
              <v-icon color="white" size="22" left>
                mdi-cog-outline
              </v-icon>
              Brand Config
            </v-btn>
          </div>
        </div>

        <div class="flex-grow-1 d-flex flex-column justify-center overflow-hidden">
          <div v-if="isLoadingSlideData && !isExporting" class="export-loading-overlay">
            <div class="exporting-loading-content">
              <div class="exporting-loading-spinner">
                <div class="spinner-ring"></div>
                <div class="spinner-ring"></div>
                <div class="spinner-ring"></div>
              </div>
              <div class="exporting-loading-title">
                Loading Slide Data
              </div>
              <div class="exporting-loading-subtitle">
                Fetching brand comparison data...
              </div>
              <div class="exporting-loading-steps">
                <span class="active">● Preparing slides</span>
                <span :class="{ active: isLoadingSlideData }">● Processing brand data</span>
                <span>● Rendering charts</span>
              </div>
            </div>
          </div>

          <div v-if="isExporting" class="export-loading-overlay">
            <div class="exporting-loading-content">
              <div class="exporting-loading-spinner">
                <div class="spinner-ring"></div>
                <div class="spinner-ring"></div>
                <div class="spinner-ring"></div>
              </div>
              <div class="exporting-loading-title">
                Exporting PDF
              </div>
              <div class="exporting-loading-subtitle">
                Capturing slide <strong>{{ exportCurrentSlide }}</strong> of <strong>{{ slides.length }}</strong>
              </div>
              <div class="exporting-loading-steps">
                <span :class="{ active: exportCurrentSlide >= 1 }">
                  ● Preparing slides
                </span>
                <span :class="{ active: exportCurrentSlide >= Math.ceil(slides.length / 2) }">
                  ● Capturing content
                </span>
                <span :class="{ active: exportCurrentSlide >= slides.length }">
                  ● Generating PDF
                </span>
              </div>
            </div>
          </div>

          <div class="carousel-viewport mt-9">
            <div
              class="carousel-track"
              :style="{
                transform: `translateX(${
                  -currentSlideIndex * 700 +
                  (viewportWidth/2 - (slideOffset + currentSlideIndex * 155))
                }px)`
              }"
            >
              <div
                v-for="(slide, index) in slides"
                :key="index"
                class="slide-item"
                :class="{ 'is-active': currentSlideIndex == index }"
                :style="navbarWithName ? 'width: 750px !important;' : 'width: 850px !important;'"
                @click="switchSlide(index)"
              >
                <div class="card-slide elevation-10">
                  <div class="draft-watermark-overlay" aria-hidden="true">
                    <div class="draft-watermark-grid">
                      <div v-for="n in 30" :key="'wm-' + n" class="draft-watermark-cell">
                        <img
                          src="/Watermark-Logo-MedAn.png"
                          class="draft-wm-logo"
                          alt="Watermark logo MedAn247"
                        />
                      </div>
                    </div>
                  </div>
                  <v-card class="fill-height d-flex flex-column" flat>
                    <div class="d-flex w-100">
                      <div style="flex: 8.6; position: relative; z-index: 5;">
                        <v-card-title class="d-flex align-center pt-6 pr-0 pl-6 pb-0">
                          <div class="d-flex align-center">
                            <v-avatar
                              class="rounded-lg mr-4"
                              color="#2B4A6F"
                              :size="navbarWithName ? 52 : 56"
                            >
                              <v-icon
                                v-if="slide.icon.startsWith('mdi')"
                                color="white"
                                :size="navbarWithName ? 28 : 32"
                              >
                                {{ slide.icon }}
                              </v-icon>

                              <i
                                v-else
                                :class="slide.icon"
                                :style="navbarWithName ? 'font-size: 20px; color: white;' : 'font-size: 24px; color: white;'"
                              ></i>
                            </v-avatar>
                            <div>
                              <div
                                class="font-weight-bold color-navy"
                                :class="navbarWithName ? 'text-subtitle-1' : 'text-h6'"
                                :style="navbarWithName ? 'line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 420px;' : ''"
                              >
                                {{ slide.title }}
                              </div>
                              <div class="text-caption grey--text text--darken-1">
                                {{ slide.subtitle }}
                              </div>
                            </div>
                          </div>
                        </v-card-title>

                        <v-card-text class="flex-grow-1 d-flex align-center justify-center pt-0 px-4 pb-4">
                          <div v-if="slide.id == 'pulse'" class="w-full">
                            <svg
                              height="100%"
                              width="100%"
                              viewBox="0 0 720 450"
                              preserveAspectRatio="xMidYMid meet"
                              style="overflow: visible;"
                            >
                              <g stroke="#CFD8DC" stroke-width="1">
                                <line
                                  v-for="(label, i) in yAxisLabels"
                                  :key="'grid-' + i"
                                  x1="50"
                                  :y1="300 - (label / yMax) * 250"
                                  x2="700"
                                  :y2="300 - (label / yMax) * 250"
                                />
                              </g>

                              <g font-size="12" fill="#263238" font-weight="bold">
                                <g v-for="(label, i) in yAxisLabels" :key="'y-' + i">
                                  <text
                                    x="40"
                                    :y="300 - (label / yMax) * 250 + 5"
                                    text-anchor="end"
                                  >
                                    {{ label }}
                                  </text>
                                </g>

                                <text
                                  x="25"
                                  y="170"
                                  transform="rotate(-90, 15, 175)"
                                  text-anchor="middle"
                                  fill="#000"
                                  font-weight="bold"
                                >
                                  Sentiment Δ (negative to positive)
                                </text>
                              </g>

                              <g v-for="line in lines" :key="line.name">
                                <polyline
                                  fill="none"
                                  :stroke="line.color"
                                  stroke-width="2"
                                  :points="line.points"
                                />

                                <g v-for="(p, i) in getPoints(line.points)" :key="line.name + i">
                                  <circle
                                    :cx="p.x"
                                    :cy="p.y"
                                    :r="hoverPoint?.line == line.name && hoverPoint?.index == i ? 7 : 4"
                                    :fill="line.color"
                                    stroke="white"
                                    stroke-width="2"
                                    style="cursor: pointer; transition: all 0.2s ease"
                                    @mouseenter="handleHoverExecutivePulse(i, p)"
                                    @mouseleave="handleLeaveExecutivePulse"
                                  />
                                </g>
                              </g>

                              <g stroke="#CFD8DC" stroke-width="1">
                                <line
                                  v-for="(label, i) in volumeAxisLabels"
                                  :key="'vgrid-' + i"
                                  x1="50"
                                  :y1="395 - (label / volumeMax) * 80"
                                  x2="700"
                                  :y2="395 - (label / volumeMax) * 80"
                                />
                              </g>

                              <g fill="#2B4A6F">
                                <rect
                                  v-for="(bar, i) in weeklyVolume"
                                  :key="'bar-' + i"
                                  :x="bar.x"
                                  :y="bar.y + 45"
                                  :height="bar.height"
                                  :width="bar.bw"
                                  rx="2"
                                  fill="#2B4A6F"
                                  style="cursor: pointer; transition: all 0.2s ease"
                                  @mouseenter="handleHoverExecutivePulse(i, { ...bar, x: bar.x + bar.bw / 2 })"
                                  @mouseleave="handleLeaveExecutivePulse"
                                />
                              </g>

                              <g font-size="12" fill="#263238" font-weight="bold">
                                <text
                                  v-for="(label, i) in volumeAxisLabels"
                                  :key="'vlabel-' + i"
                                  x="40"
                                  :y="395 - (label / volumeMax) * 80 + 5"
                                  text-anchor="end"
                                >
                                  {{ label }}
                                </text>
                              </g>

                              <text
                                y="320"
                                transform="rotate(-90, 15, 325)"
                                text-anchor="middle"
                                font-size="12"
                                fill="#000"
                                font-weight="bold"
                              >
                                Weekly Volume
                              </text>

                              <g font-size="12" fill="#263238" font-weight="bold">
                                <text
                                  v-for="(d, i) in emotionTimeSeries"
                                  :key="i"
                                  :x="70 + (i * chartSpacing)"
                                  y="410"
                                  text-anchor="middle"
                                >
                                  {{ d.date }}
                                </text>
                              </g>

                              <line
                                v-if="activeTooltipExecutivePulse"
                                :x1="activeTooltipExecutivePulse.x"
                                y1="50"
                                :x2="activeTooltipExecutivePulse.x"
                                y2="400"
                                stroke="#90A4AE"
                                stroke-width="1"
                                stroke-dasharray="4"
                              />

                              <g v-if="activeTooltipExecutivePulse">
                                <rect
                                  :x="activeTooltipExecutivePulse.x - 45"
                                  :y="activeTooltipExecutivePulse.y - 55"
                                  height="26"
                                  width="90"
                                  rx="8"
                                  fill="white"
                                  stroke="#CBD5E1"
                                  stroke-width="1.5"
                                  filter="url(#tooltipShadow)"
                                />
                                <text
                                  :x="activeTooltipExecutivePulse.x"
                                  :y="activeTooltipExecutivePulse.y - 38"
                                  text-anchor="middle"
                                  fill="#1E293B"
                                  font-size="12"
                                  font-weight="bold"
                                >
                                  {{ activeTooltipExecutivePulse.date }}
                                </text>

                                <g v-for="(k, i) in activeTooltipExecutivePulse.keywords" :key="'kw-' + i">
                                  <rect
                                    :x="activeTooltipExecutivePulse.x - getKeywordTooltipWidth() - 15"
                                    :y="activeTooltipExecutivePulse.y - 5 + (i * 28)"
                                    height="22"
                                    :width="getKeywordTooltipWidth()"
                                    rx="8"
                                    fill="white"
                                    stroke="#CBD5E1"
                                    stroke-width="1.5"
                                    filter="url(#tooltipShadow)"
                                  />
                                  <text
                                    :x="activeTooltipExecutivePulse.x - getKeywordTooltipWidth() / 2 - 15"
                                    :y="activeTooltipExecutivePulse.y + 10 + (i * 28)"
                                    text-anchor="middle"
                                    fill="#1E293B"
                                    font-size="12"
                                    font-weight="bold"
                                  >
                                    {{ k }}
                                  </text>
                                </g>

                                <g font-size="13" fill="#1E293B">
                                  <rect
                                    :x="activeTooltipExecutivePulse.x + 38"
                                    :y="activeTooltipExecutivePulse.y - 28 - (getBrandNameLines().length - 1) * 16"
                                    :height="emotionOrder.length * 20 + 50 + (getBrandNameLines().length - 1) * 16"
                                    :width="getTooltipPulseRightWidth()"
                                    rx="10"
                                    fill="white"
                                    stroke="#CBD5E1"
                                    stroke-width="1.5"
                                    filter="url(#tooltipShadow)"
                                  />

                                  <g v-for="(name, i) in getBrandNameLines()" :key="'brand-' + i">
                                    <text
                                      :x="activeTooltipExecutivePulse.x + 50"
                                      :y="activeTooltipExecutivePulse.y - 9 - (getBrandNameLines().length - 1 - i) * 18"
                                      fill="#1E293B"
                                      font-weight="bold"
                                    >
                                      {{ name }}
                                    </text>
                                  </g>

                                  <text
                                    v-if="activeTooltipExecutivePulse.volume !== null"
                                    :x="activeTooltipExecutivePulse.x + 50"
                                    :y="activeTooltipExecutivePulse.y + 10"
                                    fill="#1E293B"
                                    font-weight="bold"
                                  >
                                    Volume:
                                    <tspan>{{ activeTooltipExecutivePulse.volume }}</tspan>
                                  </text>

                                  <g v-for="(emotion, index) in emotionOrder" :key="emotion">
                                    <text
                                      :x="activeTooltipExecutivePulse.x + 50"
                                      :y="activeTooltipExecutivePulse.y + 28 + (index * 20)"
                                      fill="#1E293B"
                                      font-weight="bold"
                                    >
                                      {{ emotion }}:
                                      <tspan :fill="emotionColorMap[emotion]">
                                        {{ activeTooltipExecutivePulse.values[emotion] ?? 0 }}
                                      </tspan>
                                    </text>
                                  </g>
                                </g>

                                <defs>
                                  <filter id="tooltipShadow" x="-20%" y="-20%" width="140%" height="140%">
                                    <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#94A3B8" flood-opacity="0.2" />
                                  </filter>
                                </defs>
                              </g>
                            </svg>
                          </div>

                          <div v-if="slide.id == 'gap'" class="w-full">
                            <svg height="100%" width="100%" viewBox="0 0 780 520" preserveAspectRatio="xMidYMid meet">
                              <g fill="none" stroke="#A6A6A6" stroke-width="1">
                                <polygon points="400,70 527,123 580,250 527,377 400,430 273,377 220,250 273,123" />
                                <polygon points="400,106 502,148 544,250 502,352 400,394 298,352 256,250 298,148" />
                                <polygon points="400,142 476,174 508,250 476,326 400,358 324,326 292,250 324,174" />
                                <polygon points="400,178 451,200 472,250 451,300 400,322 349,300 328,250 349,200" />
                                <polygon points="400,214 425,225 436,250 425,275 400,286 375,275 364,250 375,225" />
                              </g>

                              <g stroke="#A6A6A6" stroke-width="1">
                                <line x1="400" y1="250" x2="400" y2="70" />
                                <line x1="400" y1="250" x2="527" y2="123" />
                                <line x1="400" y1="250" x2="580" y2="250" />
                                <line x1="400" y1="250" x2="527" y2="377" />
                                <line x1="400" y1="250" x2="400" y2="430" />
                                <line x1="400" y1="250" x2="273" y2="377" />
                                <line x1="400" y1="250" x2="220" y2="250" />
                                <line x1="400" y1="250" x2="273" y2="123" />
                              </g>

                              <g v-for="(brand, bIdx) in spiderData" :key="'polygon-'+bIdx">
                                <polygon
                                  :points="brand.points"
                                  :fill="(brand.isMain || activeTooltipSentimentGap?.brandIndex == bIdx) ? '#2B4A6F' : 'none'"
                                  :fill-opacity="0.15"
                                  :stroke="(brand.isMain || activeTooltipSentimentGap?.brandIndex == bIdx) ? '#2B4A6F' : '#455A64'"
                                  :stroke-width="(brand.isMain || activeTooltipSentimentGap?.brandIndex == bIdx) ? 3 : 2"
                                  :stroke-dasharray="(brand.isMain || activeTooltipSentimentGap?.brandIndex == bIdx) ? '' : '4,3'"
                                  style="pointer-events: none; transition: all 0.2s ease;"
                                />
                              </g>

                              <g
                                font-size="15"
                                font-weight="bold"
                                fill="#333"
                                text-anchor="middle"
                                style="pointer-events: none;"
                              >
                                <text
                                  v-for="(label, i) in emotionOrder"
                                  :key="'label-'+i"
                                  :x="getLabelPosition(i).x"
                                  :y="getLabelPosition(i).y"
                                >
                                  {{ label }}
                                </text>
                              </g>

                              <g v-for="(brand, bIdx) in spiderData" :key="'circles-'+bIdx" :fill="brand.isMain ? '#2B4A6F' : '#455A64'">
                                <circle
                                  v-for="(pt, pIdx) in brand.coords"
                                  :key="'c-'+bIdx+'-'+pIdx"
                                  :cx="pt.x"
                                  :cy="pt.y"
                                  :r="activeTooltipSentimentGap?.brandIndex == bIdx && activeTooltipSentimentGap?.pointIndex == pIdx ? 7 : (brand.isMain ? 4 : 3)"
                                  style="cursor: pointer; transition: all 0.2s ease"
                                  @mouseenter="handleHoverSentimentGap(brand, bIdx, pIdx, pt)"
                                  @mouseleave="handleLeaveSentimentGap"
                                />
                              </g>

                              <g v-if="activeTooltipSentimentGap" style="pointer-events: none;">
                                <rect
                                  :x="activeTooltipSentimentGap.x + 20"
                                  :y="activeTooltipSentimentGap.y - 50 - (getSentimentGapBrandNameLines().length - 1) * 18"
                                  :height="emotionOrder.length * 25 + 60 + (getSentimentGapBrandNameLines().length - 1) * 18"
                                  :width="getTooltipSpiderWidth()"
                                  rx="10"
                                  fill="white"
                                  stroke="#CBD5E1"
                                  stroke-width="1.5"
                                  filter="url(#tooltipShadowGap)"
                                />

                                <g v-for="(name, i) in getSentimentGapBrandNameLines()" :key="'sg-brand-' + i">
                                  <text
                                    :x="activeTooltipSentimentGap.x + 30"
                                    :y="activeTooltipSentimentGap.y - 27 - (getSentimentGapBrandNameLines().length - 1 - i) * 20"
                                    fill="#1E293B"
                                    font-size="15"
                                    font-weight="bold"
                                  >
                                    {{ name }}
                                  </text>
                                </g>

                                <text
                                  v-if="activeTooltipSentimentGap.volume !== undefined"
                                  :x="activeTooltipSentimentGap.x + 30"
                                  :y="activeTooltipSentimentGap.y - 3"
                                  fill="#1E293B"
                                  font-size="14"
                                  font-weight="bold"
                                >
                                  Volume:
                                  <tspan>
                                    {{ activeTooltipSentimentGap.volume }}
                                  </tspan>
                                </text>

                                <g v-for="(emotion, index) in emotionOrder" :key="emotion">
                                  <text
                                    v-if="activeTooltipSentimentGap.values[emotion] !== undefined"
                                    :x="activeTooltipSentimentGap.x + 30"
                                    :y="activeTooltipSentimentGap.y + 20 + (index * 25)"
                                    fill="#1E293B"
                                    font-size="14"
                                    font-weight="bold"
                                  >
                                    {{ emotion }}:
                                    <tspan :fill="emotionColorMap[emotion]">
                                      {{ activeTooltipSentimentGap.values[emotion] }}
                                    </tspan>
                                  </text>
                                </g>
                              </g>
                            </svg>
                          </div>

                          <div v-if="slide.id == 'velocity'" class="w-full" style="position: relative;">
                            <svg
                              width="100%"
                              viewBox="0 0 820 560"
                              xmlns="http://www.w3.org/2000/svg"
                              style="overflow: visible; position: relative; z-index: 5;"
                            >
                              <defs>
                                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                                  <polygon points="0 0, 10 3.5, 0 7" fill="#B71C1C" />
                                </marker>
                                <clipPath id="clipZones">
                                  <rect x="435" y="30" width="350" height="230" />
                                  <rect x="435" y="260" width="350" height="230" />
                                </clipPath>
                              </defs>

                              <linearGradient id="greenGrad" x1="0%" y1="100%" x2="100%" y2="0%">
                                <stop offset="0%" style="stop-color: #E8F5E9; stop-opacity: 1" />
                                <stop offset="100%" style="stop-color: #388E3C; stop-opacity: 1" />
                              </linearGradient>
                              <rect x="435" y="30" width="350" height="230" fill="url(#greenGrad)" opacity="1"/>

                              <linearGradient id="redGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style="stop-color: #FFEBEE; stop-opacity: 1" />
                                <stop offset="100%" style="stop-color: #B71C1C; stop-opacity: 1" />
                              </linearGradient>
                              <rect x="435" y="260" width="350" height="230" fill="url(#redGrad)" opacity="1"/>

                              <g opacity="0.45" style="pointer-events: none;" clip-path="url(#clipZones)">
                                <image
                                  href="/Watermark-Logo-MedAn-Transparent-Background.png"
                                  x="455"
                                  y="50"
                                  height="100"
                                  width="155"
                                  transform="rotate(-30, 527, 95)"
                                  preserveAspectRatio="xMidYMid meet"
                                />
                                <image
                                  href="/Watermark-Logo-MedAn-Transparent-Background.png"
                                  x="570"
                                  y="150"
                                  height="100"
                                  width="155"
                                  transform="rotate(-30, 642, 195)"
                                  preserveAspectRatio="xMidYMid meet"
                                />
                                <image
                                  href="/Watermark-Logo-MedAn-Transparent-Background.png"
                                  x="455"
                                  y="280"
                                  height="100"
                                  width="155"
                                  transform="rotate(-30, 527, 325)"
                                  preserveAspectRatio="xMidYMid meet"
                                />
                                <image
                                  href="/Watermark-Logo-MedAn-Transparent-Background.png"
                                  x="570"
                                  y="380"
                                  height="100"
                                  width="155"
                                  transform="rotate(-30, 642, 425)"
                                  preserveAspectRatio="xMidYMid meet"
                                />
                              </g>

                              <g stroke="#E0E0E0" stroke-width="1">
                                <line x1="85" y1="30" x2="785" y2="30" />
                                <line x1="85" y1="76" x2="785" y2="76" />
                                <line x1="85" y1="122" x2="785" y2="122" />
                                <line x1="85" y1="168" x2="785" y2="168" />
                                <line x1="85" y1="214" x2="785" y2="214" />
                                <line x1="85" y1="306" x2="785" y2="306" />
                                <line x1="85" y1="352" x2="785" y2="352" />
                                <line x1="85" y1="398" x2="785" y2="398" />
                                <line x1="85" y1="444" x2="785" y2="444" />
                                <line x1="85" y1="490" x2="785" y2="490" />

                                <line x1="172.5" y1="30" x2="172.5" y2="490" />
                                <line x1="260" y1="30" x2="260" y2="490" />
                                <line x1="347.5" y1="30" x2="347.5" y2="490" />
                                <line x1="522.5" y1="30" x2="522.5" y2="490" />
                                <line x1="610" y1="30" x2="610" y2="490" />
                                <line x1="697.5" y1="30" x2="697.5" y2="490" />
                              </g>

                              <line x1="435" y1="30"  x2="435" y2="498" stroke="#000" stroke-width="2" />
                              <line x1="75"  y1="260" x2="795" y2="260" stroke="#000" stroke-width="2" />

                              <g font-size="13" fill="#666" text-anchor="end">
                                <text x="75" y="34">
                                  +50%
                                </text>
                                <text x="75" y="80">
                                  +40%
                                </text>
                                <text x="75" y="126">
                                  +30%
                                </text>
                                <text x="75" y="172">
                                  +20%
                                </text>
                                <text x="75" y="218">
                                  +10%
                                </text>
                                <text x="75" y="264">
                                  0%
                                </text>
                                <text x="75" y="310">
                                  -10%
                                </text>
                                <text x="75" y="356">
                                  -20%
                                </text>
                                <text x="75" y="402">
                                  -30%
                                </text>
                                <text x="75" y="448">
                                  -40%
                                </text>
                                <text x="75" y="494">
                                  -50%
                                </text>
                              </g>

                              <g font-size="13" fill="#666" text-anchor="middle">
                                <text x="85" y="512">
                                  Low
                                </text>
                                <text x="260" y="512">
                                  ↑
                                </text>
                                <text x="435" y="512">
                                  Mid
                                </text>
                                <text x="610" y="512">
                                  ↑
                                </text>
                                <text x="785" y="512">
                                  High
                                </text>
                              </g>

                              <text
                                transform="rotate(-90, 18, 260)"
                                x="18"
                                y="260"
                                text-anchor="middle"
                                font-size="12"
                                font-weight="bold"
                                fill="#000"
                              >
                                EMOTION SHIFT Δ % (NEGATIVE to POSITIVE)
                              </text>

                              <text
                                x="435"
                                y="540"
                                text-anchor="middle"
                                font-size="12"
                                font-weight="bold"
                                fill="#222"
                              >
                                VOLUME (MENTIONS per WEEK)
                              </text>

                              <text x="95" y="20" font-size="14" font-weight="bold" fill="#333">
                                Engagement Intensity
                              </text>
                              <text x="95" y="505" font-size="14" font-weight="bold" fill="#333">
                                Negative Shift
                              </text>

                              <g
                                v-for="(bubble, i) in velocityBubbles"
                                :key="'bubble-' + i"
                                class="svg-bubble"
                                @mouseenter="handleHoverVelocity(bubble)"
                                @mouseleave="handleLeaveVelocity"
                                style="cursor: pointer;"
                              >
                                <circle
                                  :cx="bubble.x"
                                  :cy="bubble.cy560"
                                  :r="activeTooltipVelocity?.name == bubble.name ? bubble.r + 3 : bubble.r"
                                  :fill="bubble.fill"
                                  stroke="white"
                                  stroke-width="2"
                                  opacity="1"
                                  style="transition: all 0.2s ease"
                                />

                                <rect
                                  :x="bubble.x - getLabelWidth(bubble.name) / 2"
                                  :y="bubble.cy560 + bubble.r + 2"
                                  :width="getLabelWidth(bubble.name)"
                                  height="20"
                                  rx="6"
                                  ry="6"
                                  :fill="bubble.fill"
                                  stroke="white"
                                  stroke-width="1.5"
                                  opacity="1"
                                  style="transition: all 0.2s ease"
                                />
                                <text
                                  :x="bubble.x"
                                  :y="bubble.cy560 + bubble.r + 15"
                                  text-anchor="middle"
                                  font-size="11"
                                  font-weight="bold"
                                  fill="white"
                                >
                                  {{ bubble.name }}
                                </text>
                              </g>

                              <rect x="595" y="0" width="175" height="30" rx="8" fill="#388E3C" />
                              <text x="682" y="20" text-anchor="middle" font-size="12" font-weight="bold" fill="white">
                                VIRAL SUCCESS ZONE
                              </text>

                              <rect x="450" y="490" width="190" height="30" rx="8" fill="#B71C1C" />
                              <text x="545" y="510" text-anchor="middle" font-size="12" font-weight="bold" fill="white">
                                BREAKOUT ISSUE ZONE
                              </text>

                              <g
                                v-if="activeTooltipVelocity && activeTooltipVelocity.hiddenBubbles?.length > 0"
                                @mouseenter="handleEnterOverlapPanel"
                                @mouseleave="handleLeaveOverlapPanel"
                              >
                                <g
                                  :transform="`translate(
                                    ${getTooltipXPosition()},
                                    ${activeTooltipVelocity.cy560
                                      - activeTooltipVelocity.r
                                      - (getOverflowPanelLayout().height + 70)
                                      - 16}
                                  )`"
                                >
                                  <rect
                                    x="0"
                                    y="0"
                                    :height="getOverflowPanelLayout().height + 68"
                                    :width="getOverflowPanelLayout().width"
                                    rx="12"
                                    fill="white"
                                    stroke="#E2E8F0"
                                    stroke-width="1.5"
                                    @mouseenter="() => {}"
                                    @mouseleave="() => {}"
                                  />

                                  <text
                                    :x="getOverflowPanelLayout().width / 2"
                                    y="28"
                                    text-anchor="middle"
                                    font-size="16"
                                    font-weight="bold"
                                    fill="#1E293B"
                                    style="pointer-events: none;"
                                  >
                                    {{ activeTooltipVelocity.hiddenBubbles.length }} overlapping keywords
                                  </text>

                                  <text
                                    :x="getOverflowPanelLayout().width / 2"
                                    y="48"
                                    text-anchor="middle"
                                    font-size="14"
                                    fill="#64748B"
                                    style="pointer-events: none;"
                                  >
                                    More keywords are not visible
                                  </text>

                                  <text
                                    :x="getOverflowPanelLayout().width / 2"
                                    y="64"
                                    text-anchor="middle"
                                    font-size="14"
                                    fill="#64748B"
                                    style="pointer-events: none;"
                                  >
                                    due to overlapping bubbles.
                                  </text>

                                  <line
                                    x1="20"
                                    y1="75"
                                    :x2="getOverflowPanelLayout().width - 20"
                                    y2="75"
                                    stroke="#CBD5E1"
                                    stroke-width="1"
                                    style="pointer-events: none;"
                                  />

                                  <g
                                    v-for="(pb, pi) in getOverflowPanelLayout().bubbles"
                                    :key="'pb-' + pi"
                                    style="cursor: pointer;"
                                    @mouseenter="handleHoverOverlapBubble(pb, getTooltipXPosition(), activeTooltipVelocity.cy560 - activeTooltipVelocity.r - (getOverflowPanelLayout().height + 70) - 16)"
                                    @mouseleave="handleLeaveOverlapBubble"
                                  >
                                    <circle
                                      :cx="pb.px"
                                      :cy="pb.py + 72"
                                      :r="activeTooltipOverlapBubble?.name == pb.name ? pb.r + 3 : pb.r"
                                      :fill="pb.fill"
                                      stroke="white"
                                      stroke-width="2"
                                      opacity="1"
                                      style="transition: all 0.2s ease;"
                                    />

                                    <rect
                                      :x="pb.px - getLabelWidth(pb.name) / 2"
                                      :y="pb.py + 72 + pb.r + 2"
                                      :width="getLabelWidth(pb.name)"
                                      height="18"
                                      rx="5"
                                      ry="5"
                                      :fill="pb.fill"
                                      stroke="white"
                                      stroke-width="1.5"
                                    />

                                    <text
                                      :x="pb.px"
                                      :y="pb.py + 72 + pb.r + 14"
                                      text-anchor="middle"
                                      font-size="10"
                                      font-weight="bold"
                                      fill="white"
                                      style="pointer-events: none;"
                                    >
                                      {{ pb.name }}
                                    </text>
                                  </g>
                                </g>
                              </g>

                              <g
                                v-if="activeTooltipOverlapBubble"
                                style="pointer-events: none;"
                              >
                                <rect
                                  :x="activeTooltipOverlapBubble.tx"
                                  :y="activeTooltipOverlapBubble.ty - 50"
                                  :height="getTooltipOverlapBubbleHeight()"
                                  :width="getTooltipOverlapBubbleWidth()"
                                  rx="10"
                                  fill="white"
                                  stroke="#CBD5E1"
                                  stroke-width="1.5"
                                  filter="url(#tooltipShadow)"
                                />

                                <text
                                  :x="activeTooltipOverlapBubble.tx + 10"
                                  :y="activeTooltipOverlapBubble.ty - 26"
                                  fill="#1E293B"
                                  font-size="16"
                                  font-weight="bold"
                                >
                                  {{ activeTooltipOverlapBubble.name }}
                                </text>

                                <text
                                  :x="activeTooltipOverlapBubble.tx + 10"
                                  :y="activeTooltipOverlapBubble.ty - 3"
                                  fill="#1E293B"
                                  font-size="15"
                                  font-weight="bold"
                                >
                                  Brand: <tspan>{{ (activeTooltipOverlapBubble.brands ?? []).join(', ') }}</tspan>
                                </text>

                                <text
                                  :x="activeTooltipOverlapBubble.tx + 10"
                                  :y="activeTooltipOverlapBubble.ty + 20"
                                  fill="#1E293B"
                                  font-size="15"
                                  font-weight="bold"
                                >
                                  Volume: <tspan>{{ activeTooltipOverlapBubble.totalVolume }} mentions</tspan>
                                </text>

                                <text
                                  :x="activeTooltipOverlapBubble.tx + 10"
                                  :y="activeTooltipOverlapBubble.ty + 43"
                                  fill="#1E293B"
                                  font-size="15"
                                  font-weight="bold"
                                >
                                  Emotion Shift: <tspan>{{ activeTooltipOverlapBubble.emotionShift ? activeTooltipOverlapBubble.emotionShift.toFixed(1) : 0 }}%</tspan>
                                </text>

                                <text
                                  :x="activeTooltipOverlapBubble.tx + 10"
                                  :y="activeTooltipOverlapBubble.ty + 66"
                                  fill="#1E293B"
                                  font-size="15"
                                  font-weight="bold"
                                >
                                  Sentiment:
                                  <tspan :fill="sentimentColorMap[activeTooltipOverlapBubble.sentiment]">
                                    {{ activeTooltipOverlapBubble.sentiment }}
                                  </tspan>
                                </text>

                                <g
                                  v-for="(emotion, index) in getActiveEmotionsFromBubble(activeTooltipOverlapBubble)"
                                  :key="'ovlp-emo-' + emotion.name"
                                >
                                  <text
                                    :x="activeTooltipOverlapBubble.tx + 10"
                                    :y="activeTooltipOverlapBubble.ty + 88 + (index * 22)"
                                    fill="#1E293B"
                                    font-size="15"
                                    font-weight="bold"
                                  >
                                    {{ emotion.name }}:
                                    <tspan :fill="emotionColorMap[emotion.name]">
                                      {{ emotion.value }}
                                    </tspan>
                                  </text>
                                </g>
                              </g>

                              <g
                                v-if="activeTooltipVelocity && !activeTooltipOverlapBubble"
                                style="pointer-events: none;"
                              >
                                <rect
                                  :x="activeTooltipVelocity.tx"
                                  :y="activeTooltipVelocity.ty - 50"
                                  :height="getTooltipVelocityHeight()"
                                  :width="getTooltipVelocityWidth()"
                                  rx="10"
                                  fill="white"
                                  stroke="#CBD5E1"
                                  stroke-width="1.5"
                                />

                                <text
                                  :x="activeTooltipVelocity.tx + 10"
                                  :y="activeTooltipVelocity.ty - 26"
                                  fill="#1E293B"
                                  font-size="16"
                                  font-weight="bold"
                                >
                                  {{ activeTooltipVelocity.name }}
                                </text>

                                <text
                                  :x="activeTooltipVelocity.tx + 10"
                                  :y="activeTooltipVelocity.ty - 3"
                                  fill="#1E293B"
                                  font-size="15"
                                  font-weight="bold"
                                >
                                  Brand: <tspan>{{ (activeTooltipVelocity.brands ?? []).join(', ') }}</tspan>
                                </text>

                                <text
                                  :x="activeTooltipVelocity.tx + 10"
                                  :y="activeTooltipVelocity.ty + 20"
                                  fill="#1E293B"
                                  font-size="15"
                                  font-weight="bold"
                                >
                                  Volume: <tspan>{{ activeTooltipVelocity.totalVolume }} mentions</tspan>
                                </text>

                                <text
                                  :x="activeTooltipVelocity.tx + 10"
                                  :y="activeTooltipVelocity.ty + 43"
                                  fill="#1E293B"
                                  font-size="15"
                                  font-weight="bold"
                                >
                                  Emotion Shift: <tspan>{{ activeTooltipVelocity.emotionShift ? activeTooltipVelocity.emotionShift.toFixed(1) : 0 }}%</tspan>
                                </text>

                                <text
                                  :x="activeTooltipVelocity.tx + 10"
                                  :y="activeTooltipVelocity.ty + 66"
                                  fill="#1E293B"
                                  font-size="15"
                                  font-weight="bold"
                                >
                                  Sentiment:
                                  <tspan :fill="sentimentColorMap[activeTooltipVelocity.sentiment]">
                                    {{ activeTooltipVelocity.sentiment }}
                                  </tspan>
                                </text>

                                <g v-for="(emotion, index) in getActiveEmotions()" :key="'vel-emo-' + emotion.name">
                                  <text
                                    :x="activeTooltipVelocity.tx + 10"
                                    :y="activeTooltipVelocity.ty + 88 + (index * 22)"
                                    fill="#1E293B"
                                    font-size="15"
                                    font-weight="bold"
                                  >
                                    {{ emotion.name }}:
                                    <tspan :fill="emotionColorMap[emotion.name]">
                                      {{ emotion.value }}
                                    </tspan>
                                  </text>
                                </g>
                              </g>
                            </svg>
                          </div>
                        </v-card-text>
                      </div>

                      <div class="mt-6 ml-1 mr-4" style="flex: 3.4;">
                        <v-card class="rounded-lg overflow-hidden" flat>
                          <div class="d-flex align-center px-4 py-3" style="background-color: #C5D8EB;">
                            <v-icon color="#233E5E" size="24" class="mr-2">
                              mdi-lightbulb-on-outline
                            </v-icon>
                            <span class="text-subtitle-1 font-weight-bold" style="font-size: 1.1rem !important; color: #233E5E;">
                              AI insight
                            </span>
                          </div>

                          <div
                            class="pa-4"
                            style="background-color: #DEE9F4; color: #1A2E44; height: 365px; overflow-y: auto;"
                          >
                            <div class="text-body-2" style="line-height: 1.5; font-size: 0.875rem;">
                              <div v-html="slide.insight"></div>
                            </div>

                            <div v-if="slide.oar" class="d-flex flex-wrap mt-4" style="gap: 8px;">
                              <v-chip
                                v-for="item in oarItems"
                                :key="item.key"
                                @click="openOarDialog(item, slide.oar)"
                                class="font-weight-medium"
                                style="background-color: #233E5E; color: white; cursor: pointer;"
                                size="small"
                              >
                                {{ item.label }}
                              </v-chip>
                            </div>

                            <div v-if="slide.topics && slide.topics.length" class="mt-4">
                              <div
                                v-for="(topic, idx) in slide.topics"
                                :key="idx"
                                class="d-flex align-center mb-2"
                                style="gap: 8px; cursor: pointer;"
                                @click="openOarDialogFull(topic)"
                              >
                                <span
                                  class="px-2 py-1 rounded font-weight-bold"
                                  :style="`background-color: ${riskColor[topic.risk_level] || '#607D8B'}; color: white; font-size: 0.7rem;`"
                                >
                                  {{ topic.risk_level }}
                                </span>
                                <span class="font-weight-medium">
                                  {{ topic.topic_name }}
                                </span>
                                <span class="ml-1" style="font-size: 0.75rem; color: #607D8B;">
                                  ({{ topic.type }})
                                </span>
                              </div>
                            </div>
                          </div>
                        </v-card>
                      </div>

                      <v-dialog v-model="oarDialog" max-width="480">
                        <v-card class="rounded-lg" style="overflow: hidden;">
                          <div class="d-flex align-center px-4 py-3" style="background-color: #233E5E;">
                            <span class="text-subtitle-1 font-weight-bold" style="color: white;">
                              {{ oarDialogTitle }}
                            </span>
                            <v-spacer></v-spacer>
                            <v-btn icon size="small" variant="text" @click="oarDialog = false">
                              <v-icon color="white">
                                mdi-close
                              </v-icon>
                            </v-btn>
                          </div>
                          <v-card-text class="pa-4" style="color: #1A2E44; line-height: 1.6;">
                            {{ oarDialogContent }}
                          </v-card-text>
                        </v-card>
                      </v-dialog>

                      <v-dialog v-model="oarDialogFull" max-width="520">
                        <v-card class="rounded-lg" style="overflow: hidden;">
                          <div class="d-flex align-center px-4 py-3" style="background-color: #233E5E;">
                            <span class="text-subtitle-1 font-weight-bold" style="color: white;">
                              {{ oarDialogFullTitle }}
                            </span>
                            <v-spacer></v-spacer>
                            <v-btn icon size="small" variant="text" @click="oarDialogFull = false">
                              <v-icon color="white">
                                mdi-close
                              </v-icon>
                            </v-btn>
                          </div>
                          <v-card-text class="pa-4" style="color: #1A2E44; line-height: 1.6;">
                            <div class="mb-4">
                              <div class="font-weight-bold mb-1" style="color: #233E5E;">
                                Observation
                              </div>
                              <div>{{ oarDialogFullContent.observation }}</div>
                            </div>
                            <div class="mb-4">
                              <div class="font-weight-bold mb-1" style="color: #233E5E;">
                                Attribution
                              </div>
                              <div>{{ oarDialogFullContent.attribution }}</div>
                            </div>
                            <div>
                              <div class="font-weight-bold mb-1" style="color: #233E5E;">
                                Recommendation
                              </div>
                              <div>{{ oarDialogFullContent.recommendation }}</div>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-dialog>
                    </div>
                  </v-card>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex justify-center mt-7 pb-10">
            <div
              v-for="(_, index) in slides"
              :key="'dot-'+index"
              class="nav-dot"
              :class="{ active: currentSlideIndex == index }"
              @click="switchSlide(index)"
            ></div>
          </div>
        </div>
      </v-card>

      <div>
        <Notification ref="alert"></Notification>
      </div>

      <SettingModal v-model="settingDialog" />
    </v-container>
  </v-app>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from "axios";
import { decryptionData, trackEvent } from '@/components/utils/utils.js';
import Notification from '@/components/Notification';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import SettingModal from '@/views/BrandAnalysis/components/SettingModal.vue';

export default {
  components: {
    Notification: Notification,
    SettingModal: SettingModal,
  },
  data() {
    return {
      selectedGroupAnalysisKey: "",
      selectedGroupAnalysis: null,
      itemGroupAnalysis: [],
      isLoadingSlideData: false,
      isExporting: false,
      exportCurrentSlide: 0,
      settingDialog: false,
      currentSlideIndex: 0,
      uiScale: 1,
      viewportWidth: 0,
      oarDialog: false,
      oarDialogTitle: '',
      oarDialogContent: '',
      oarDialogFull: false,
      oarDialogFullTitle: '',
      oarDialogFullContent: { observation: '', attribution: '', recommendation: '' },
      riskColor: {
        CRITICAL: '#B3261E',
        HIGH: '#D97706',
        MEDIUM: '#1565C0',
        LOW: '#2E7D32'
      },
      slides: [
        {
          id: 'pulse',
          title: 'The Executive Pulse (Macro-Perspective)',
          subtitle: 'Professional-grade Interactive Narrative Timeline',
          icon: 'fa-solid fa-arrow-up-right-dots',
          insight: '',
          oar: null
        },
        {
          id: 'gap',
          title: 'The Sentiment Gap (Competitive Benchmarking)',
          subtitle: 'Competitive Radar Chart',
          icon: 'mdi-spider-web',
          insight: '',
          oar: null
        },
        {
          id: 'velocity',
          title: 'Topic Velocity: Weekly Volume & Emotion Δ Shift',
          subtitle: 'Professional dashboard mockup visualization',
          icon: 'fa-solid fa-arrow-up-right-dots',
          insight: '',
          oar: null
        }
      ],
      yAxisLabels: [],
      lines: [],
      hoverPoint: null,
      volumeAxisLabels: [0, 0, 0],
      volumeMax: 0,
      weeklyVolume: [],
      emotionTimeSeries: [],
      activeTooltipExecutivePulse: null,
      emotionOrder: [
        'Joy','Trust','Anticipation','Surprise',
        'Sadness','Fear','Anger','Disgust'
      ],
      emotionColorMap: {
        Joy: '#FFD700',
        Trust: '#1E88E5',
        Anticipation: '#00897B',
        Surprise: '#FB8C00',
        Sadness: '#546E7A',
        Fear: '#6A1B9A',
        Anger: '#E53935',
        Disgust: '#7CB342'
      },
      spiderData: [],
      radarCenter: { x: 400, y: 250 },
      radarMaxRadius: 180,
      activeTooltipSentimentGap: null,
      velocityBubbles: [],
      activeTooltipVelocity: null,
      leaveVelocityTimer: null,
      activeTooltipOverlapBubble: null,
      sentimentColorMap: {
        'Negative': '#B71C1C',
        'Positive': '#388E3C',
        'Neutral': '#2B4A6F'
      },
    }
  },
  mounted() {
    this.loadGroupAnalysis();
    this.updateWidthSlide();
    window.addEventListener('resize', this.updateWidthSlide);
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWidthSlide);
    window.removeEventListener('keydown', this.handleKeydown);
  },
  computed: {
    ...mapGetters(['navbarWithName']),
    slideOffset() {
      if (this.navbarWithName && this.currentSlideIndex == 0) return 500;
      if (this.navbarWithName && this.currentSlideIndex == 1) return 400;
      if (this.navbarWithName && this.currentSlideIndex == 2) return 300;
      return 545;
    },
    chartSpacing() {
      const n = this.emotionTimeSeries.length;
      const usableWidth = 630;
      return n > 1
        ? Math.min(100, Math.floor(usableWidth / (n - 1)))
        : 100;
    },
    oarItems() {
      return [
        { key: 'observation', label: 'Observation' },
        { key: 'attribution', label: 'Attribution' },
        { key: 'recommendation', label: 'Recommendation' }
      ];
    }
  },
  watch: {
    navbarWithName() {
      this.$nextTick(() => {
        this.updateWidthSlide();
      });
    }
  },
  methods: {
    async loadAllSlideData() {
      trackEvent('brand-analysis-apply-slide-data', {
        title: `Brand Analysis Apply Slide Data - ${this.selectedGroupAnalysisKey}`,
        brand: this.selectedGroupAnalysisKey
      });

      this.isLoadingSlideData = true;

      try {
        await Promise.all([
          this.loadBrandComparisonDay(),
          this.loadBrandComparisonWeek(),
          this.loadAIInsight()
        ]);
      } finally {
        this.isLoadingSlideData = false;
      }
    },
    async loadBrandComparisonDay() {
      try {
        const authToken = localStorage.getItem("token_app");
        if (!authToken) {
          throw new Error("Auth token not found");
        }

        const mainBrand = this.selectedGroupAnalysis?.mainBrand ?? [];
        const competitors = this.selectedGroupAnalysis?.competitors ?? [];
        const allBrands = [...mainBrand, ...competitors];

        const brandNames = allBrands.map(p => p.USER_DISPLAY_NAME);
        const mainBrandNames = mainBrand.map(p => (p.USER_DISPLAY_NAME || '').trim());

        const payload = {
          api_type: "day",
          brand_name: brandNames,
          start_time: "10-08-2026",
          end_time: "16-08-2026",
        };

        const response = await axios.post('/brand_analysis/comparison', payload, {
          headers: {
            'auth-token': authToken,
          },
        });

        const json = response.data;
        const brands = json.data ?? [];

        const mainBrandsData = brands.filter(b => mainBrandNames.includes((b.brand_name || '').trim()));

        const brand = mainBrandsData.length > 0
          ? this.combineMainBrandsData(mainBrandsData)
          : brands[0];

        if (brand) {
          this.emotionTimeSeries = this.mapEmotionTimeSeries(brand.dates, brand.brand_name);

          this.generateYAxis();
          this.lines = this.generateLines();

          const maxVolume = Math.max(...brand.dates.map(d => d.day_volume));
          this.generateVolumeAxis(maxVolume);

          this.weeklyVolume = brand.dates.map((d, i) => {
            const sp = this.chartSpacing;
            const bw = Math.min(40, sp - 12);
            const x = 70 + (i * sp) - bw / 2;
            const height = (d.day_volume / this.volumeMax) * 80;
            return {
              x,
              y: 350 - height,
              height,
              value: d.day_volume,
              bw
            };
          });
        } else {
          this.emotionTimeSeries = [];
          this.lines = [];
          this.weeklyVolume = [];
        }

        const categoryMap = {};
        allBrands.forEach(p => { categoryMap[p.USER_DISPLAY_NAME] = p.NAME_CATEGORY; });
        this.calculateSpiderData(brands, categoryMap);
      } catch (err) {
        console.error('Error loadBrandComparisonDay:', err);
      }
    },
    combineMainBrandsData(mainBrandsData) {
      if (mainBrandsData.length === 1) {
        return mainBrandsData[0];
      }

      const combinedName = mainBrandsData.map(b => (b.brand_name || '').trim()).join(', ');

      const dateSet = new Set();
      mainBrandsData.forEach(b => (b.dates || []).forEach(d => dateSet.add(d.date)));
      const allDates = Array.from(dateSet).sort();

      const combinedDates = allDates.map(date => {
        let totalVolume = 0;
        const emotionTotals = {};
        const brandKeywords = [];

        mainBrandsData.forEach(b => {
          const dayData = (b.dates || []).find(d => d.date === date);
          if (!dayData) return;

          totalVolume += dayData.day_volume || 0;

          brandKeywords.push({
            brand: (b.brand_name || '').trim(),
            keywords: this.getTopKeywords(dayData.emotion, 2)
          });

          (dayData.emotion || []).forEach(e => {
            if (!emotionTotals[e.emotion]) {
              emotionTotals[e.emotion] = { emotion: e.emotion, count: 0, keywords: [] };
            }
            emotionTotals[e.emotion].count += e.count || 0;
            if (Array.isArray(e.keywords) && e.keywords.length) {
              emotionTotals[e.emotion].keywords.push(...e.keywords);
            }
          });
        });

        return {
          date,
          day_volume: totalVolume,
          emotion: Object.values(emotionTotals),
          brandKeywords
        };
      });

      return {
        brand_name: combinedName,
        dates: combinedDates
      };
    },
    async loadAIInsight() {
      this.resetSlideInsights();

      try {
        const axiosInstance = axios.create({
          baseURL: '/apigenerativeAI',
        });

        const payload = {
          "start_time": "10-08-2026",
          "end_time": "16-08-2026",
          "group_analysis": this.selectedGroupAnalysisKey
        };

        const response = await axiosInstance.post('/gen-ai-medan/api/v1/chat/describe-brand-analysis', payload);

        const summary = response?.data?.data?.summary;
        if (!summary) return;

        if (summary.executive_pulse) {
          this.setSlideInsight('pulse', {
            summary: summary.executive_pulse.brand_positioning_summary,
            strategy: summary.executive_pulse.strategy_recommendation,
            oar: summary.executive_pulse.oar_analysis
          });
        }

        if (summary.sentiment_gap) {
          this.setSlideInsight('gap', {
            summary: summary.sentiment_gap.analysis_summary,
            strategy: summary.sentiment_gap.strategy_recommendation,
            oar: summary.sentiment_gap.oar_analysis
          });
        }

        if (summary.topic_velocity) {
          this.setSlideInsightTopicVelocity(summary.topic_velocity);
        }
      } catch (err) {
        console.error('Error loadAIInsight:', err);
      }
    },
    resetSlideInsights() {
      this.slides.forEach(slide => {
        slide.insight = '';
        slide.oar = null;

        if (slide.id === 'velocity') {
          slide.topics = [];
        }
      });
    },
    setSlideInsight(slideId, { summary, strategy, oar }) {
      const slide = this.slides.find(s => s.id === slideId);
      if (!slide) return;

      slide.insight = `
        <p class="mb-4">
          ${summary}
        </p>
        <p class="mb-0">
          <span class="font-weight-bold">Strategi:</span> ${strategy}
        </p>
      `;

      slide.oar = oar || null;
    },
    setSlideInsightTopicVelocity(topicVelocity) {
      const slide = this.slides.find(s => s.id === 'velocity');
      if (!slide) return;

      slide.insight = `
        <p class="mb-3">
          ${topicVelocity.analysis_summary}
        </p>
        <p class="mb-3">
          <span class="font-weight-bold">Strategi:</span> ${topicVelocity.strategy_recommendation}
        </p>
        <div class="mb-2 font-weight-bold" style="font-size: 0.85rem;">Topik Prioritas:</div>
      `;

      slide.topics = topicVelocity.prioritized_topics || [];
      slide.oar = null;
    },
    openOarDialog(item, oarData, topicName = null) {
      if (!oarData) return;

      this.oarDialogTitle = topicName ? `${item.label} — ${topicName}` : item.label;
      this.oarDialogContent = oarData[item.key] || '-';
      this.oarDialog = true;
    },
    openOarDialogFull(topic) {
      if (!topic?.oar_analysis) return;

      this.oarDialogFullTitle = topic.topic_name;
      this.oarDialogFullContent = {
        observation: topic.oar_analysis.observation || '-',
        attribution: topic.oar_analysis.attribution || '-',
        recommendation: topic.oar_analysis.recommendation || '-'
      };
      this.oarDialogFull = true;
    },
    mapEmotionTimeSeries(dates, brandName) {
      return dates.map(d => {
        const values = {};

        (d.emotion || []).forEach(e => {
          values[this.capitalize(e.emotion)] = e.count;
        });

        let keywords = [];

        if (Array.isArray(d.brandKeywords) && d.brandKeywords.length > 1) {
          const orderedNames = this.sortBrandsByOrder(d.brandKeywords.map(b => b.brand));
          const ordered = orderedNames
            .map(n => d.brandKeywords.find(b => b.brand === n))
            .filter(Boolean);

          for (let round = 0; round < 2 && keywords.length < 2; round++) {
            ordered.forEach(b => {
              const kw = b.keywords[round];
              if (kw && !keywords.includes(kw) && keywords.length < 2) {
                keywords.push(kw);
              }
            });
          }
        } else {
          keywords = this.getTopKeywords(d.emotion, 2);
        }

        return {
          date: d.date,
          values,
          keywords,
          volume: d.day_volume,
          brandName
        };
      });
    },
    getTopKeywords(emotions, limit = 2) {
      const keywordCount = {};

      (emotions || []).forEach(e => {
        (e.keywords || []).forEach(k => {
          keywordCount[k.keyword] = (keywordCount[k.keyword] || 0) + (k.count || 0);
        });
      });

      return Object.entries(keywordCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([keyword]) => keyword);
    },
    generateYAxis() {
      let maxVal = 0;
      this.emotionTimeSeries.forEach(d => {
        Object.values(d.values).forEach(v => { if (v > maxVal) maxVal = v; });

        if (Object.keys(d.values).length == 0 && d.volume > maxVal) {
          maxVal = d.volume;
        }
      });

      if (maxVal == 0) {
        this.yMax = 5;
        this.yAxisLabels = [5, 4, 3, 2, 1, 0];
        return;
      }

      const roughStep = maxVal / 5;
      const step = this.niceStep(roughStep);
      const niceMax = Math.ceil(maxVal / step) * step;

      this.yMax = niceMax;
      this.yAxisLabels = [];
      for (let v = niceMax; v >= 0; v -= step) {
        this.yAxisLabels.push(Math.round(v));
      }
    },
    niceStep(roughStep) {
      const candidates = [];
      for (let mag = 0.1; mag <= 1000000; mag *= 10) {
        candidates.push(1 * mag, 2 * mag, 5 * mag);
      }
      candidates.sort((a, b) => a - b);

      const below = candidates.filter(c => c <= roughStep);
      return below.length ? below[below.length - 1] : candidates[0];
    },
    generateLines() {
      return this.emotionOrder.map(emotion => {
        const points = this.emotionTimeSeries.map((d, i) => {
          const x = 70 + (i * this.chartSpacing);

          const hasAnyEmotion = Object.keys(d.values).length > 0;
          const val = hasAnyEmotion ? (d.values[emotion] || 0) : (d.volume || 0);

          const chartHeight = 250;
          const y = 300 - (val / this.yMax) * chartHeight;

          return `${x},${y}`;
        }).join(' ');

        return {
          name: emotion,
          color: this.emotionColorMap[emotion],
          points
        }
      });
    },
    getPoints(points) {
      return points.split(' ').map(p => {
        const [x, y] = p.split(',')
        return { x: +x, y: +y }
      });
    },
    handleHoverExecutivePulse(index, point) {
      const data = this.emotionTimeSeries[index];

      this.activeTooltipExecutivePulse = {
        x: point.x,
        y: point.y,
        date: data.date,
        values: data.values,
        keywords: data.keywords,
        volume: data.volume,
        brandName: data.brandName
      }
    },
    getBrandNameLines() {
      if (!this.activeTooltipExecutivePulse?.brandName) return [];

      const names = this.activeTooltipExecutivePulse.brandName
        .split(',')
        .map(name => name.trim())
        .filter(Boolean);

      const mainBrandOrder = (this.selectedGroupAnalysis?.mainBrand ?? [])
        .map(p => (p.USER_DISPLAY_NAME || '').trim());

      return names.slice().sort((a, b) => {
        const idxA = mainBrandOrder.indexOf(a);
        const idxB = mainBrandOrder.indexOf(b);
        return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
      });
    },
    handleLeaveExecutivePulse() {
      this.activeTooltipExecutivePulse = null;
    },
    getKeywordTooltipWidth() {
      if (!this.activeTooltipExecutivePulse?.keywords?.length) return 64;
      const longest = this.activeTooltipExecutivePulse.keywords.reduce(
        (a, b) => a.length > b.length ? a : b, ''
      );
      return Math.max(64, longest.length * 7 + 15);
    },
    getTooltipPulseRightWidth() {
      if (!this.activeTooltipExecutivePulse) return 130;

      const charWidth = 8;
      const padding = 20;

      const brandNameWidths = this.getBrandNameLines().map(name => name.length * charWidth + padding);
      const volumeWidth = `Volume: ${this.activeTooltipExecutivePulse.volume}`.length * charWidth + padding;

      const emotionWidths = this.emotionOrder.map(emotion => {
        const val = this.activeTooltipExecutivePulse.values[emotion] ?? 0;
        return `${emotion}: ${val}`.length * charWidth + padding;
      });

      const maxWidth = Math.max(...brandNameWidths, volumeWidth, ...emotionWidths);
      return Math.max(110, maxWidth);
    },
    generateVolumeAxis(maxVolume) {
      const targetIntervals = 4;
      const roughStep = maxVolume / targetIntervals;

      let step = this.niceStep(roughStep);
      let niceMax = Math.ceil(maxVolume / step) * step;

      while (Math.round(niceMax / step) > targetIntervals) {
        step = this.niceStepAbove(step);
        niceMax = Math.ceil(maxVolume / step) * step;
      }

      this.volumeMax = niceMax;
      this.volumeAxisLabels = [];
      for (let v = niceMax; v >= 0; v -= step) {
        this.volumeAxisLabels.push(Math.round(v));
      }
    },
    niceStepAbove(step) {
      const candidates = [];
      for (let mag = 0.1; mag <= 1000000; mag *= 10) {
        candidates.push(1 * mag, 2 * mag, 5 * mag);
      }
      candidates.sort((a, b) => a - b);
      const above = candidates.filter(c => c > step + 0.0001);
      return above.length ? above[0] : step * 2;
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    calculateSpiderData(brands, categoryMap = {}) {
      let globalMax = 0;

      const getCategoryFor = (brandName) => {
        const trimmed = (brandName || '').trim();
        if (categoryMap[brandName] !== undefined) return categoryMap[brandName];
        const foundKey = Object.keys(categoryMap).find(k => (k || '').trim() === trimmed);
        return foundKey ? categoryMap[foundKey] : undefined;
      };

      const mainBrands = [];
      const competitorBrands = [];

      brands.forEach(brand => {
        const category = getCategoryFor(brand.brand_name);
        const isMain = category?.toLowerCase().includes('main');
        if (isMain) {
          mainBrands.push(brand);
        } else {
          competitorBrands.push(brand);
        }
      });

      const aggregateBrand = (brandList, isMain) => {
        const totals = {};
        this.emotionOrder.forEach(e => totals[e] = 0);

        let totalVolume = 0;

        brandList.forEach(brand => {
          (brand.dates || []).forEach(d => {
            totalVolume += d.day_volume || 0;

            (d.emotion || []).forEach(e => {
              const key = this.capitalize(e.emotion);
              if (totals[key] !== undefined) {
                totals[key] += e.count;
              }
            });
          });
        });

        Object.values(totals).forEach(v => {
          if (v > globalMax) globalMax = v;
        });

        const name = brandList
          .map(b => (b.brand_name || '').trim())
          .join(', ');

        return { name, totals, volume: totalVolume, isMain };
      };

      const brandAggregates = [];

      if (mainBrands.length > 0) {
        brandAggregates.push(aggregateBrand(mainBrands, true));
      }

      competitorBrands.forEach(brand => {
        brandAggregates.push(aggregateBrand([brand], false));
      });

      this.spiderData = brandAggregates.map(brand => {
        const coords = this.emotionOrder.map((emotion, i) => {
          const val = brand.totals[emotion] || 0;
          const ratio = globalMax > 0 ? (val / globalMax) : 0;
          const angle = (i * (2 * Math.PI / 8)) - (Math.PI / 2);

          return {
            x: this.radarCenter.x + (this.radarMaxRadius * ratio) * Math.cos(angle),
            y: this.radarCenter.y + (this.radarMaxRadius * ratio) * Math.sin(angle)
          };
        });

        return {
          name: brand.name,
          totals: brand.totals,
          volume: brand.volume,
          isMain: brand.isMain,
          coords: coords,
          points: coords.map(c => `${c.x},${c.y}`).join(' ')
        };
      });
    },
    handleHoverSentimentGap(brand, bIdx, pIdx, pt) {
      this.activeTooltipSentimentGap = {
        x: pt.x,
        y: pt.y,
        brandName: brand.name,
        brandIndex: bIdx,
        pointIndex: pIdx,
        values: brand.totals,
        volume: brand.volume
      };
    },
    handleLeaveSentimentGap() {
      this.activeTooltipSentimentGap = null;
    },
    getLabelPosition(i) {
      const angle = (i * (2 * Math.PI / 8)) - (Math.PI / 2);
      const extraRadius = i === 2 ? 50 : 30;
      const labelRadius = this.radarMaxRadius + extraRadius;
      return {
        x: this.radarCenter.x + labelRadius * Math.cos(angle),
        y: this.radarCenter.y + labelRadius * Math.sin(angle)
      };
    },
    getSentimentGapBrandNameLines() {
      if (!this.activeTooltipSentimentGap?.brandName) return [];

      const names = this.activeTooltipSentimentGap.brandName
        .split(',')
        .map(name => name.trim())
        .filter(Boolean);

      return this.sortBrandsByOrder(names);
    },
    getTooltipSpiderWidth() {
      if (!this.activeTooltipSentimentGap) return 160;

      const brandNameLines = this.getSentimentGapBrandNameLines();

      const texts = [
        ...brandNameLines,
        ...this.emotionOrder.map(e => `${e}: ${this.activeTooltipSentimentGap.values[e] ?? ''}`)
      ];

      const longest = texts.reduce((a, b) => a.length > b.length ? a : b, '');

      return Math.max(160, longest.length * 7 + 40);
    },
    async loadBrandComparisonWeek() {
      try {
        const authToken = localStorage.getItem("token_app");
        if (!authToken) {
          throw new Error("Auth token not found");
        }

        const mainBrand = this.selectedGroupAnalysis?.mainBrand ?? [];
        const brandNames = mainBrand.map(p => p.USER_DISPLAY_NAME);

        const payload = {
          api_type: "week",
          brand_name: brandNames,
          start_time: "10-08-2026",
          end_time: "16-08-2026",
        };

        const response = await axios.post('/brand_analysis/comparison', payload, {
          headers: {
            'auth-token': authToken,
          },
        });

        const json = response.data;

        this.calculateVelocityBubbles(json.data);
      } catch (err) {
        console.error('Error loadBrandComparisonWeek:', err);
      }
    },
    getBrandOrderMap() {
      const mainBrand = this.selectedGroupAnalysis?.mainBrand ?? [];
      const competitors = this.selectedGroupAnalysis?.competitors ?? [];
      const orderedNames = [...mainBrand, ...competitors].map(p => (p.USER_DISPLAY_NAME || '').trim());

      const orderMap = {};
      orderedNames.forEach((name, idx) => { orderMap[name] = idx; });
      return orderMap;
    },
    sortBrandsByOrder(brandsArray) {
      const orderMap = this.getBrandOrderMap();
      return [...brandsArray].sort((a, b) => {
        const idxA = orderMap[(a || '').trim()] ?? Infinity;
        const idxB = orderMap[(b || '').trim()] ?? Infinity;
        return idxA - idxB;
      });
    },
    calculateVelocityBubbles(data) {
      const svgXMin = 85;
      const svgXMax = 785;
      const svgYTop = 30;
      const svgYBottom = 490;

      const keywordMap = {};

      data.forEach(brand => {
        const keywords =
          brand.tag_cloud_engagement
            ?.tag_cloud_engagement_sentiment
            ?.keywords ?? [];

        keywords.forEach(kw => {
          const key = kw.keyword;
          if (!keywordMap[key]) {
            keywordMap[key] = {
              keyword: key,
              totalVolume: 0,
              totalEngagement: 0,
              sentimentSum: 0,
              count: 0,
              brands: new Set(),
            };
          }

          const entry = keywordMap[key];
          entry.totalVolume += kw.volume;
          entry.totalEngagement += kw.engagement;
          entry.sentimentSum += kw.sentiment;
          entry.count += 1;
          entry.brands.add((brand.brand_name || '').trim());
        });
      });

      let keywords = Object.values(keywordMap).map(entry => {
        const avgSentiment = entry.sentimentSum / entry.count;
        const emotionShift = (avgSentiment - 0.5) * 100;

        return {
          keyword: entry.keyword,
          brands: this.sortBrandsByOrder([...entry.brands]),
          totalVolume: entry.totalVolume,
          totalEngagement: entry.totalEngagement,
          avgSentiment,
          emotionShift: Math.max(-50, Math.min(50, emotionShift)),
        };
      });

      keywords = keywords.filter(k => k.totalVolume >= 2);

      const volumes = keywords.map(k => k.totalVolume);
      const minVol = Math.min(...volumes);
      const maxVol = Math.max(...volumes);
      const volRange = maxVol - minVol || 1;

      const minBubbleRadius = 10;
      const maxBubbleRadius = 32;
      const xPad = 50;

      const getBubbleColor = (emotionShift) => {
        if (emotionShift > 0) return this.sentimentColorMap['Positive'];
        if (emotionShift < 0) return this.sentimentColorMap['Negative'];
        return this.sentimentColorMap['Neutral'];
      };

      const getSentiment = (emotionShift) => {
        if (emotionShift > 0) return 'Positive';
        if (emotionShift < 0) return 'Negative';
        return 'Neutral';
      };

      this.velocityBubbles = keywords.map(k => {
        const xNorm = (k.totalVolume - minVol) / volRange;
        const x = (svgXMin + xPad) + xNorm * (svgXMax - svgXMin - xPad * 2);

        const yNorm = (k.emotionShift - (-50)) / 100;
        const rawCy = svgYBottom - yNorm * (svgYBottom - svgYTop);

        const rNorm = (k.totalVolume - minVol) / volRange;
        const r = Math.round(minBubbleRadius + rNorm * (maxBubbleRadius - minBubbleRadius));

        const cy560 = Math.round(Math.max(svgYTop + r, Math.min(svgYBottom - r, rawCy)));
        const clampedX = Math.round(Math.max(svgXMin + r, Math.min(svgXMax - r, x)));

        return {
          name: k.keyword,
          x: clampedX,
          cy560,
          r,
          fill: getBubbleColor(k.emotionShift),
          sentiment: getSentiment(k.emotionShift),
          totalVolume: k.totalVolume,
          totalEngagement: k.totalEngagement,
          emotionShift: k.emotionShift,
          emotionValues: {},
          brands: k.brands,
        };
      });
    },
    getLabelWidth(name) {
      return name.length * 6 + 16;
    },
    handleHoverVelocity(bubble) {
      if (this.leaveVelocityTimer) {
        clearTimeout(this.leaveVelocityTimer);
        this.leaveVelocityTimer = null;
      }

      const offsetX = 12;
      const tx = bubble.x + bubble.r + offsetX;

      const otherOverlapping = this.velocityBubbles.filter(b => {
        if (b.name == bubble.name) return false;
        const dx = bubble.x - b.x;
        const dy = bubble.cy560 - b.cy560;
        const dist = Math.sqrt(dx * dx + dy * dy);
        return dist < (bubble.r + b.r);
      });

      const hiddenBubbles = otherOverlapping.length > 0
        ? [bubble, ...otherOverlapping]
        : [];

      this.activeTooltipVelocity = {
        ...bubble,
        tx,
        ty: bubble.cy560,
        hiddenBubbles,
      };
    },
    getTooltipXPosition() {
      if (!this.activeTooltipVelocity) return 0;

      const panelWidth = this.getOverflowPanelLayout().width;
      const idealX = this.activeTooltipVelocity.x - panelWidth / 2;

      const minLeftPadding = 16;
      return Math.max(minLeftPadding, idealX);
    },
    getOverflowPanelLayout() {
      const allBubbles = [...(this.activeTooltipVelocity.hiddenBubbles || [])];

      const totalItems = allBubbles.length;
      const maxCols = 4;
      const cols = totalItems < maxCols ? Math.max(1, totalItems) : maxCols;

      const bubbleRadius = 18;
      const paddingX = 24;
      const paddingY = 36;
      const rowHeight = 72;

      let maxLabelTextWidth = 0;
      allBubbles.forEach(b => {
        const w = this.getLabelWidth(b.name) || 0;
        if (w > maxLabelTextWidth) {
          maxLabelTextWidth = w;
        }
      });

      const colWidth = Math.max(76, maxLabelTextWidth + 16);

      const rows = Math.ceil(totalItems / cols);
      let panelWidth = cols * colWidth + paddingX * 2;
      const panelHeight = rows * rowHeight + paddingY;

      if (totalItems == 1) {
        panelWidth = Math.max(240, panelWidth);
      }

      const bubbles = allBubbles.map((b, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);

        let px;
        if (totalItems == 1) {
          px = panelWidth / 2;
        } else {
          px = paddingX + col * colWidth + colWidth / 2;
        }

        const py = paddingY + row * rowHeight;

        return {
          ...b,
          px,
          py,
          r: bubbleRadius,
        };
      });

      return { width: panelWidth, height: panelHeight, bubbles };
    },
    handleLeaveVelocity() {
      this.leaveVelocityTimer = setTimeout(() => {
        this.activeTooltipVelocity = null;
        this.activeTooltipOverlapBubble = null;
        this.leaveVelocityTimer = null;
      }, 300);
    },
    getActiveEmotions() {
      if (!this.activeTooltipVelocity) return [];
      const values = this.activeTooltipVelocity.emotionValues || {};
      return this.emotionOrder
        .filter(e => {
          const val = values[e.toLowerCase()] ?? values[e] ?? 0;
          return val > 0;
        })
        .map(e => ({
          name: e,
          value: values[e.toLowerCase()] ?? values[e]
        }));
    },
    getTooltipVelocityHeight() {
      if (!this.activeTooltipVelocity) return 120;
      const activeCount = this.getActiveEmotions().length;
      const headerHeight = 110;
      const rowHeight = 20;
      const sentimentRowHeight = 20;
      return headerHeight + sentimentRowHeight + (activeCount * rowHeight);
    },
    getTooltipVelocityWidth() {
      if (!this.activeTooltipVelocity) return 180;

      const brandText = `Brand: ${(this.activeTooltipVelocity.brands ?? []).join(', ')}`;

      const texts = [
        this.activeTooltipVelocity.name,
        brandText,
        `Volume: ${this.activeTooltipVelocity.totalVolume} mentions`,
        `Emotion Shift: ${this.activeTooltipVelocity.emotionShift?.toFixed(1) ?? 0}%`,
        `Sentiment: ${this.activeTooltipVelocity.sentiment ?? ''}`,
        ...this.emotionOrder.map(e =>
          `${e}: ${this.activeTooltipVelocity.emotionValues?.[e.toLowerCase()] ?? ''}`
        )
      ];

      const charWidthMap = { narrow: 5, normal: 8.5, wide: 11 };

      const measureText = (text) => {
        let width = 0;
        for (const ch of text) {
          if ('il1|:.!, '.includes(ch)) width += charWidthMap.narrow;
          else if ('mwMW'.includes(ch)) width += charWidthMap.wide;
          else width += charWidthMap.normal;
        }
        return width;
      };

      const maxTextWidth = Math.max(...texts.map(measureText));
      return Math.max(180, maxTextWidth + 20);
    },
    handleEnterOverlapPanel() {
      if (this.leaveVelocityTimer) {
        clearTimeout(this.leaveVelocityTimer);
        this.leaveVelocityTimer = null;
      }
    },
    handleLeaveOverlapPanel() {
      this.leaveVelocityTimer = setTimeout(() => {
        this.activeTooltipVelocity = null;
        this.activeTooltipOverlapBubble = null;
        this.leaveVelocityTimer = null;
      }, 300);
    },
    handleHoverOverlapBubble(pb, panelX, panelY) {
      if (this.leaveVelocityTimer) {
        clearTimeout(this.leaveVelocityTimer);
        this.leaveVelocityTimer = null;
      }

      this.activeTooltipOverlapBubble = {
        ...pb,
        tx: panelX + pb.px + pb.r + 12,
        ty: panelY + pb.py + 72,
      };
    },
    handleLeaveOverlapBubble() {
      this.activeTooltipOverlapBubble = null;
      this.leaveVelocityTimer = setTimeout(() => {
        this.activeTooltipVelocity = null;
        this.leaveVelocityTimer = null;
      }, 300);
    },
    getActiveEmotionsFromBubble(bubble) {
      if (!bubble) return [];
      const values = bubble.emotionValues || {};
      return this.emotionOrder
        .filter(e => {
          const val = values[e.toLowerCase()] ?? values[e] ?? 0;
          return val > 0;
        })
        .map(e => ({
          name: e,
          value: values[e.toLowerCase()] ?? values[e]
        }));
    },
    getTooltipOverlapBubbleHeight() {
      if (!this.activeTooltipOverlapBubble) return 120;
      const activeCount = this.getActiveEmotionsFromBubble(this.activeTooltipOverlapBubble).length;
      return 110 + 20 + (activeCount * 20);
    },
    getTooltipOverlapBubbleWidth() {
      if (!this.activeTooltipOverlapBubble) return 180;
      const pb = this.activeTooltipOverlapBubble;
      const brandText = `Brand: ${(pb.brands ?? []).join(', ')}`;
      const texts = [
        pb.name,
        brandText,
        `Volume: ${pb.totalVolume} mentions`,
        `Emotion Shift: ${pb.emotionShift?.toFixed(1) ?? 0}%`,
        `Sentiment: ${pb.sentiment ?? ''}`,
      ];
      const charWidthMap = { narrow: 5, normal: 8.5, wide: 11 };
      const measureText = (text) => {
        let width = 0;
        for (const ch of text) {
          if ('il1|:.!, '.includes(ch)) width += charWidthMap.narrow;
          else if ('mwMW'.includes(ch)) width += charWidthMap.wide;
          else width += charWidthMap.normal;
        }
        return width;
      };
      const maxTextWidth = Math.max(...texts.map(measureText));
      return Math.max(180, maxTextWidth + 30);
    },
    async exportSlide() {
      trackEvent('export-slide-brand-analysis-success', {
        title: `Brand Analysis Export - ${this.selectedGroupAnalysisKey}`,
        brand: this.selectedGroupAnalysisKey
      });

      this.isExporting = true;
      this.exportCurrentSlide = 0;

      try {
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'px',
          format: [900, 520],
        });

        const preExportSlideIndex = this.currentSlideIndex;

        this.currentSlideIndex = 0;
        await this.$nextTick();
        await new Promise(resolve => setTimeout(resolve, 700));

        for (let i = 0; i < this.slides.length; i++) {
          this.currentSlideIndex = i;
          this.exportCurrentSlide = i + 1;
          await this.$nextTick();
          await new Promise(resolve => setTimeout(resolve, 700));

          const slideElements = this.$el.querySelectorAll('.slide-item');
          const activeSlide = slideElements[i];
          if (!activeSlide) continue;

          const cardEl = activeSlide.querySelector('.card-slide');
          if (!cardEl) continue;

          const canvas = await html2canvas(cardEl, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
          });

          const imgData = canvas.toDataURL('image/png');
          if (i > 0) pdf.addPage();

          const pdfHeight = pdf.internal.pageSize.getHeight();
          const pdfWidth = pdf.internal.pageSize.getWidth();

          const padding = 40;
          const imgHeight = pdfHeight - padding * 2;
          const imgWidth = pdfWidth - padding * 2;

          pdf.addImage(imgData, 'PNG', padding, padding, imgWidth, imgHeight);
        }

        this.currentSlideIndex = preExportSlideIndex;
        await this.$nextTick();

        pdf.save('Brand Analysis Export.pdf');
      } catch (error) {
        trackEvent('export-slide-brand-analysis-failed', {
          title: `Brand Analysis Export - ${this.selectedGroupAnalysisKey}`,
          brand: this.selectedGroupAnalysisKey
        });
        console.error('Export PDF gagal:', error);
      } finally {
        this.isExporting = false;
        this.exportCurrentSlide = 0;
      }
    },
    switchSlide(i) {
      trackEvent('brand-analysis-switch-slide', {
        title: `Brand Analysis Switch Slide - ${this.slides[i]?.title}`,
        brand: this.selectedGroupAnalysisKey
      });
      this.currentSlideIndex = i;
    },
    updateWidthSlide() {
      const baseDpr = 1;
      const dpr = window.devicePixelRatio || 1;

      const scale = baseDpr / dpr;
      this.uiScale = Math.min(Math.max(scale, 1), 1.5);

      const sidebarWidth = this.navbarWithName ? 220 : 64;
      this.viewportWidth = (window.innerWidth - sidebarWidth) / this.uiScale;
    },
    handleKeydown(e) {
      if (e.key == 'ArrowRight') {
        if (this.currentSlideIndex < this.slides.length - 1) {
          this.switchSlide(this.currentSlideIndex + 1);
        }
      } else if (e.key == 'ArrowLeft') {
        if (this.currentSlideIndex > 0) {
          this.switchSlide(this.currentSlideIndex - 1);
        }
      }
    },
    async loadGroupAnalysis() {
      axios.get("brand_analysis/group_analysis",
        { headers: { 'auth-token': localStorage.getItem('token_app') } },
        { dataType: 'json' }
      )
      .then((response) => {
        const dataDecryption = decryptionData(response.data.token);
        this.itemGroupAnalysis = dataDecryption.data ?? [];

        if (this.itemGroupAnalysis.length > 0) {
          this.selectedGroupAnalysis = this.itemGroupAnalysis[0];
          this.selectedGroupAnalysisKey = this.itemGroupAnalysis[0].GROUP_ANALYSIS;
        }

        this.loadAllSlideData();
      })
      .catch((e) => {
        if (e.response) {
          const { status } = e.response;

          switch (status) {
            case 404:
              this.$refs.alert.error_login(404);
              break;
            case 403:
              this.$refs.alert.error_login(403);
              break;
            default:
              console.log("some other error");
              break;
          }
        } else {
          this.alert = true;
          setTimeout(() => {
            this.alert = false;
          }, 10000);
          this.message = "API Brand Analysis Group Analysis Refused To Connect";
          this.colorAlert = "red";
        }
      });
    },
    updateSelectedGroupAnalysis() {
      const found = this.itemGroupAnalysis.find(
        item => item.GROUP_ANALYSIS == this.selectedGroupAnalysisKey
      );
      if (found) {
        this.selectedGroupAnalysis = found;
      }
    },
  }
}
</script>

<style scoped>
.brand-wrapper {
  background: #FFFFFF;
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #E2E8F0;
  border-radius: 28px;
  overflow: hidden;
}

.brand-header {
  background: #FFFFFF;
  border-bottom: 1px solid #E2E8F0;
}

.header-select-wrapper {
  position: relative;
  width: 195px;
  flex-shrink: 0;
}

.header-group-select {
  background-color: white;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  color: #333;
  font-size: 14px;
  height: 47px;
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 40px 12px 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.header-group-select:focus {
  border-color: #0471A5;
  box-shadow: 0 0 0 2px rgba(4, 113, 165, 0.15);
}

.header-chevron-icon {
  position: absolute;
  pointer-events: none;
  color: #666;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
}

.header-apply-btn {
  background-color: #000 !important;
  color: white !important;
  height: 45px !important;
  border-radius: 8px !important;
  font-size: 17px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  flex-shrink: 0;
  padding: 12px 24px !important;
}

.header-export-btn {
  background-color: white !important;
  color: black !important;
  height: 45px !important;
  border: 1.5px solid #000 !important;
  border-radius: 8px !important;
  font-size: 17px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  flex-shrink: 0;
  padding: 12px 24px !important;
}

.header-setting-btn {
  background-color: #0471A5 !important;
  color: white !important;
  height: 45px !important;
  border-radius: 8px !important;
  font-size: 17px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  flex-shrink: 0;
  padding: 12px 24px !important;
}

@keyframes mdi-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.mdi-spin {
  animation: mdi-spin 1s linear infinite;
  display: inline-block;
}

.export-loading-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.82);
  border-radius: 16px;
  inset: 0;
  z-index: 100;
}

.exporting-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
  padding: 24px;
}

.exporting-loading-title {
  color: white;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.exporting-loading-subtitle {
  color: #aaaaaa;
  font-size: 13px;
  line-height: 1.6;
}

.exporting-loading-steps {
  display: flex;
  flex-direction: column;
  color: #555555;
  font-size: 13px;
  gap: 6px;
  transition: color 0.3s;
}

.exporting-loading-steps span.active {
  color: #ffffff;
  font-weight: 600;
}

.exporting-loading-spinner {
  position: relative;
  height: 60px;
  width: 60px;
}

.spinner-ring {
  position: absolute;
  animation: spinRing 1.4s linear infinite;
  border: 3px solid transparent;
  border-radius: 50%;
  inset: 0;
}

.spinner-ring:nth-child(1) {
  animation-duration: 1.0s;
  border-top-color: #ffffff;
}

.spinner-ring:nth-child(2) {
  animation-duration: 1.4s;
  animation-direction: reverse;
  border-top-color: #aaaaaa;
  inset: 8px;
}

.spinner-ring:nth-child(3) {
  animation-duration: 1.8s;
  inset: 16px;
  border-top-color: #555555;
}

@keyframes spinRing {
  to { transform: rotate(360deg); }
}

.color-navy { color: #1E293B; }
.w-full { width: 100%; }

.carousel-viewport {
  position: relative;
  overflow: visible;
  width: 100%;
}

.carousel-track {
  display: flex;
  transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  will-change: transform;
}

.slide-item {
  filter: none !important;
  margin: 0 50px;
  margin-top: 40px;
  flex-shrink: 0;
  opacity: 0.6;
  zoom: 0.9;
  transition: opacity 0.5s ease, zoom 0.5s ease;
}

.slide-item.is-active {
  opacity: 1;
  zoom: 1.05;
  margin-top: 0px;
}

.card-slide {
  height: 465px;
  width: 100%;
  border-radius: 24px;
  position: relative;
  background: white;
  overflow: hidden;
  border: 1px solid #E2E8F0;
  transition: box-shadow 0.3s ease;
}

.slide-item.is-active .card-slide {
  box-shadow: 0 25px 50px -12px rgba(43, 74, 111, 0.25) !important;
  border: 2px solid #2B4A6F;
}

.card-slide .v-card {
  position: relative;
  background: transparent !important;
  z-index: 4;
}

.draft-watermark-overlay {
  position: absolute;
  pointer-events: none;
  overflow: hidden;
  inset: 0;
  z-index: 3;
  border-radius: 24px;
}

.draft-watermark-grid {
  display: grid;
  position: absolute;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(6, 1fr);
  height: 150%;
  width: 150%;
  top: -25%;
  left: -25%;
  transform: rotate(-30deg);
  transform-origin: center center;
}

.draft-watermark-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.65;
}

.draft-wm-logo {
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
  width: 110px;
}

.nav-dot {
  height: 10px;
  width: 10px;
  margin: 0 6px;
  border-radius: 50%;
  background: #CBD5E1;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-dot.active {
  background: #2B4A6F;
  width: 30px;
  border-radius: 5px;
}

.tooltip-bubble {
  position: absolute;
  background: #263238;
  color: white;
  pointer-events: none;
  font-size: 14px;
  line-height: 1.4;
  width: 240px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  padding: 10px;
  z-index: 99;
}

.svg-bubble {
  cursor: pointer;
  transition: transform 0.3s ease;
  transform-origin: center;
  transform-box: fill-box;
}

.svg-bubble:hover {
  transform: scale(1.1);
}
</style>